import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import PropTypes from 'prop-types'
import React from 'react'

import {
  createAtHomePlan,
  fetchAtHomeListings,
  fetchAtHomeModules,
  fetchAtHomePricing,
  previewAtHomePlan,
  searchAtHomeParents
} from 'api/atHome'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import {
  apiErrorMessage,
  fmtDate,
  fmtMoney,
  fmtTime,
  FREQUENCIES,
  freqLabel,
  GRADE_RANGE_LABEL,
  isOutsideGradeRange,
  outsideGradeName,
  todayLocal
} from './format'

/**
 * Book a project (or monthly classes) on a listing: parent, child, the named
 * project, grade, sessions a week (1 to 7 since 2026-11-05 -- every extra day
 * is another listing of the SAME teacher, same category, a different weekday,
 * and free), start date. A project runs
 * for its own session count — 8 by default, 1–52 (models/module.py:28) — so
 * the weeks follow from that and the classes a week. The server previews the
 * sessions and the price; "Send payment link" books nothing until the parent
 * pays (no holds, owner 2026-10-12) — same flow as trials.
 */
const BookModuleDialog = ({ listing, open, onClose, onBooked }) => {
  const [query, setQuery] = React.useState('')
  const [options, setOptions] = React.useState([])
  const [searching, setSearching] = React.useState(false)
  const [parent, setParent] = React.useState(null)
  const [child, setChild] = React.useState('')
  const [grade, setGrade] = React.useState('')
  const [moduleId, setModuleId] = React.useState('')
  const [modules, setModules] = React.useState([])
  // The other days of the week, one listing each, after `listing` itself.
  const [extras, setExtras] = React.useState([])
  const [others, setOthers] = React.useState([])
  // {sessions a week: price} for this category; a frequency with no row is
  // refused by the server ("No price set"), so the menu says so up front.
  const [prices, setPrices] = React.useState({})
  const [startDate, setStartDate] = React.useState(todayLocal())
  const [adultOk, setAdultOk] = React.useState(false)
  const [preview, setPreview] = React.useState(null)
  const [previewError, setPreviewError] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  // Regular-style categories (Chess, Math, Kannada) are booked as monthly
  // classes: no module, one month from the start date, renewed month by month.
  const isMonthly = listing?.categoryStyle === 'regular'

  // Renewal: a held listing is booked from the day after its hold ends.
  const earliest = React.useMemo(() => {
    if (!listing?.busyUntil) return todayLocal()
    const [y, m, d] = listing.busyUntil.split('-').map(Number)
    const next = new Date(y, m - 1, d + 1)
    return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(
      2,
      '0'
    )}-${String(next.getDate()).padStart(2, '0')}`
  }, [listing])

  React.useEffect(() => {
    if (!open || !listing) return
    setQuery('')
    setOptions([])
    setParent(null)
    setChild('')
    setGrade(listing.grades?.length === 1 ? listing.grades[0] : '')
    setExtras([])
    setStartDate(earliest)
    setAdultOk(false)
    setPreview(null)
    setPreviewError('')
    setModuleId('')
    // Pairing rule (prototype, 2026-10-07): same teacher, a different day, free.
    fetchAtHomeListings({
      category: listing.category,
      teacher: listing.teacher,
      active: 1
    })
      .then((rows) =>
        setOthers(
          rows.filter(
            (r) =>
              r.uuid !== listing.uuid &&
              r.weekday !== listing.weekday &&
              (!r.busyUntil || r.busyUntil <= (listing.busyUntil || ''))
          )
        )
      )
      .catch(() => setOthers([]))
    setPrices({})
    fetchAtHomePricing()
      .then((rows) => {
        const map = {}
        ;(rows || [])
          .filter((r) => r.category === listing.category)
          .forEach((r) => {
            map[r.frequency] = r.price
          })
        setPrices(map)
      })
      .catch(() => setPrices({}))
    if (listing.categoryStyle === 'regular') {
      setModules([])
      return
    }
    fetchAtHomeModules({ category: listing.category, status: 'open' })
      .then((rows) => {
        setModules(rows)
        if (rows.length === 1) setModuleId(rows[0].uuid)
      })
      .catch(() => setModules([]))
  }, [open, listing, earliest])

  React.useEffect(() => {
    if (query.trim().length < 2) return undefined
    let active = true
    setSearching(true)
    const t = setTimeout(() => {
      searchAtHomeParents(query.trim())
        .then((res) => active && setOptions(res))
        .catch(() => active && setOptions([]))
        .finally(() => active && setSearching(false))
    }, 300)
    return () => {
      active = false
      clearTimeout(t)
    }
  }, [query])

  // Preview the sessions + price whenever the choice is complete.
  React.useEffect(() => {
    if (
      !open ||
      !listing ||
      !grade ||
      !startDate ||
      (!moduleId && !isMonthly)
    ) {
      setPreview(null)
      return undefined
    }
    let active = true
    setPreviewError('')
    previewAtHomePlan({
      listings: [listing.uuid, ...extras],
      module: isMonthly ? undefined : moduleId,
      child: child || undefined,
      grade,
      start_date: startDate
    })
      .then((p) => active && setPreview(p))
      .catch((e) => {
        if (!active) return
        setPreview(null)
        // The API's refusal is `{error: true, message: "..."}`; reading
        // `data.error` put the boolean on screen, i.e. nothing, so ops never
        // saw why (e.g. "The teacher already has a session on ..."). The
        // shared helper takes `error` only when it is a string.
        setPreviewError(apiErrorMessage(e, 'Could not preview this project.'))
      })
    return () => {
      active = false
    }
  }, [open, listing, grade, extras, startDate, moduleId, child, isMonthly])

  const children = parent?.children || []
  const selectedChild = children.find((c) => c.uuid === child)
  const selectedModule = modules.find((m) => m.uuid === moduleId)
  // Modules for the grade being booked (the child's own grade fills it in);
  // every open module until a grade is chosen. Empty grades = all grades.
  const fits = (m) =>
    !grade || !(m.grades || []).length || (m.grades || []).includes(grade)
  const fittingModules = modules.filter(fits)
  const moduleOptions = fittingModules.length ? fittingModules : modules
  // A child in a standard At Home does not run can never be booked, whatever
  // grade the admin picks below (owner, 2026-10-19).
  const canBook =
    listing &&
    parent &&
    child &&
    !isOutsideGradeRange(selectedChild) &&
    grade &&
    (moduleId || isMonthly) &&
    preview &&
    !busy

  const submit = async () => {
    setBusy(true)
    try {
      await createAtHomePlan({
        listings: [listing.uuid, ...extras],
        module: isMonthly ? undefined : moduleId,
        parent: parent.uuid,
        child,
        grade,
        start_date: startDate,
        adult_presence_acknowledged: adultOk
      })
      eventAlert(
        'Payment link sent on WhatsApp. Nothing is booked yet: the times stay open and the first payment takes them.',
        constants.SUCCESS
      )
      onBooked()
      onClose()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not book this project.'))
    } finally {
      setBusy(false)
    }
  }

  // Grades every picked day takes.
  const extraRows = extras.map((id) => others.find((o) => o.uuid === id))
  const gradeOptions = (listing?.grades || []).filter((g) =>
    extraRows.every((o) => (o?.grades || []).includes(g))
  )
  // One listing per weekday: the most days a week this teacher can give is
  // this listing plus one free listing on each other weekday.
  const otherDays = new Set(others.map((o) => o.weekday)).size
  const maxPerWeek = Math.min(FREQUENCIES.length, 1 + otherDays)
  const perWeek = 1 + extras.length
  // Weekdays already taken by `listing` and every extra except `skip`.
  const takenDays = (skip) => [
    listing?.weekday,
    ...extraRows.filter((o, i) => o && i !== skip).map((o) => o.weekday)
  ]
  // Moving to `n` a week keeps the days already picked and fills the rest
  // with the first free listing on an unused weekday.
  const choosePerWeek = (n) => {
    const kept = extras.slice(0, n - 1)
    const days = new Set([
      listing?.weekday,
      ...kept.map((id) => others.find((o) => o.uuid === id)?.weekday)
    ])
    const next = [...kept]
    others.forEach((o) => {
      if (next.length < n - 1 && !days.has(o.weekday)) {
        next.push(o.uuid)
        days.add(o.weekday)
      }
    })
    setExtras(next)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isMonthly ? 'Book monthly classes' : 'Book a project'}
      </DialogTitle>
      <DialogContent>
        {listing && (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {listing.categoryName} · {listing.teacherName} · every{' '}
            {listing.weekdayName}, {fmtTime(listing.start_time)} ·{' '}
            {listing.apartmentName}
          </Typography>
        )}
        <Autocomplete
          options={options}
          loading={searching}
          value={parent}
          onChange={(e, v) => {
            setParent(v)
            setError('')
            const only = v && v.children.length === 1 ? v.children[0] : null
            setChild(only ? only.uuid : '')
            // The child's own grade (HQ → Users) fills the grade when the listing takes it.
            if (only?.grade && (listing?.grades || []).includes(only.grade))
              setGrade(only.grade)
          }}
          onInputChange={(e, v) => setQuery(v)}
          getOptionLabel={(o) => (o ? `${o.name} · ${o.phone}` : '')}
          getOptionSelected={(o, v) => o.uuid === v.uuid}
          noOptionsText={
            query.trim().length < 2
              ? 'Type a name or phone number'
              : 'No parent found'
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Parent (name or phone)"
              margin="normal"
              fullWidth
              autoFocus
              helperText="The payment link and, once paid, the classes go to this parent's app."
            />
          )}
        />
        <TextField
          select
          label="Child attending"
          value={child}
          onChange={(e) => {
            setChild(e.target.value)
            const c = children.find((x) => x.uuid === e.target.value)
            if (c?.grade && (listing?.grades || []).includes(c.grade))
              setGrade(c.grade)
          }}
          margin="normal"
          fullWidth
          disabled={!parent}
          helperText="The child who attends. Their first name shows on the parent's 'Up next' card at home, and the teacher sees them on their session screen; the parent's session cards do not show it."
        >
          {children.map((c) => (
            <MenuItem key={c.uuid} value={c.uuid}>
              {c.name}
              {isOutsideGradeRange(c)
                ? ` · ${outsideGradeName(c)}, outside ${GRADE_RANGE_LABEL}`
                : c.grade
                ? ` · grade ${c.grade}`
                : ' · no grade on file'}
            </MenuItem>
          ))}
        </TextField>
        {/* A child outside the range At Home runs is said so outright, rather
            than reading as a grade this listing happens not to take (owner,
            2026-10-19). */}
        {isOutsideGradeRange(selectedChild) && (
          <Typography variant="caption" color="error">
            {`${selectedChild.name} is in ${outsideGradeName(
              selectedChild
            )}. At Home runs ${GRADE_RANGE_LABEL} only, so this child cannot be booked.`}
          </Typography>
        )}
        {selectedChild &&
          !selectedChild.grade &&
          !isOutsideGradeRange(selectedChild) && (
            <Typography variant="caption" color="textSecondary">
              {`${selectedChild.name} has no grade on file; add it under HQ → Users, or pick the grade below.`}
            </Typography>
          )}
        {selectedChild?.grade &&
          !isOutsideGradeRange(selectedChild) &&
          !(listing?.grades || []).includes(selectedChild.grade) && (
            <Typography variant="caption" color="error">
              {selectedChild.name} is in grade {selectedChild.grade}; this
              listing takes {(listing?.grades || []).join(', ')}.
            </Typography>
          )}
        {!isMonthly && (
          <TextField
            select
            label="Project"
            value={moduleId}
            onChange={(e) => setModuleId(e.target.value)}
            margin="normal"
            fullWidth
            helperText={
              modules.length === 0
                ? `No open ${
                    listing?.categoryName || ''
                  } project yet. Add one under Setup → Projects.`
                : 'The named set of sessions the child will do. Its name shows beside the subject on every session in the parent app.'
            }
          >
            {moduleOptions.map((m) => (
              <MenuItem key={m.uuid} value={m.uuid}>
                {m.name}
                {m.gradesLabel && m.gradesLabel !== 'All grades'
                  ? ` · ${m.gradesLabel}`
                  : ''}
                {grade && !fits(m) ? ' · not for this grade' : ''}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          label="Sessions per week"
          value={perWeek}
          onChange={(e) => choosePerWeek(Number(e.target.value))}
          margin="normal"
          fullWidth
          helperText={
            otherDays === 0
              ? `${listing?.teacherName || 'This teacher'} has no other free ${
                  listing?.categoryName || ''
                } day to pair with, so once a week only.`
              : 'How many classes a week the parent will see on their schedule. Each extra day is another weekly time with the same teacher, picked below. Only frequencies priced under Setup → Pricing can be booked.'
          }
        >
          {FREQUENCIES.map((n) => (
            <MenuItem key={n} value={n} disabled={n > maxPerWeek}>
              {freqLabel(
                n,
                listing?.categoryStyle,
                selectedModule?.sessions_count
              )}
              {prices[n] == null ? ' · no price set' : ''}
              {n > maxPerWeek ? ' · not enough free days' : ''}
            </MenuItem>
          ))}
        </TextField>
        {extras.map((id, i) => (
          <TextField
            key={i}
            select
            label={`Day ${i + 2} with the same teacher`}
            value={id}
            onChange={(e) =>
              setExtras(extras.map((x, j) => (j === i ? e.target.value : x)))
            }
            margin="normal"
            fullWidth
            helperText={
              i === 0
                ? 'Each extra class of the week must be the same teacher on a different day.'
                : undefined
            }
          >
            {others
              .filter((o) => !takenDays(i).includes(o.weekday))
              .map((o) => (
                <MenuItem key={o.uuid} value={o.uuid}>
                  {o.weekdayName} {fmtTime(o.start_time)} · {o.teacherName} ·{' '}
                  {o.apartmentName}
                </MenuItem>
              ))}
          </TextField>
        ))}
        <TextField
          select
          label="Child's grade"
          value={grade}
          onChange={(e) => setGrade(e.target.value)}
          margin="normal"
          fullWidth
          helperText={
            extras.length && gradeOptions.length === 0
              ? 'These weekly times share no grade; pick different days.'
              : 'Used to check this time (and the project) suits the child. The teacher sees it on their session screen; the parent app does not show it.'
          }
        >
          {gradeOptions.map((g) => (
            <MenuItem key={g} value={g}>
              Grade {g}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type="date"
          label="Start from"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: earliest }}
          helperText={
            isMonthly
              ? 'Classes run for one month from this date, on the listed days. Every class has to fall inside the dates this weekly time is offered for. In the parent app the earliest date on offer is six days out, and only the first eight dates that fit are offered.'
              : 'The first class is the first listed day on or after this date. Every class has to fall inside the dates this weekly time is offered for. In the parent app the earliest date on offer is six days out, and only the first eight dates that fit are offered.'
          }
        />
        {(previewError || error) && (
          <Typography variant="body2" color="error" gutterBottom>
            {error || previewError}
          </Typography>
        )}
        {preview && (
          <Typography variant="body2" style={{ marginTop: 8 }} component="div">
            <strong>
              {preview.kind === 'monthly'
                ? `Monthly classes · ${preview.sessions_total} sessions this month`
                : `${preview.moduleName} · ${preview.sessions_total} sessions over ${preview.weeks} weeks`}
            </strong>{' '}
            · {fmtDate(preview.start_date)} to {fmtDate(preview.end_date)} ·{' '}
            <strong>{fmtMoney(preview.price)}</strong>
            {preview.kind === 'monthly' ? ' for the month' : ''} before GST (
            {fmtMoney(preview.pricePerSession)} a session). The payment link
            adds GST on top of this.
            <ul
              style={{
                margin: '4px 0 0',
                paddingLeft: 18,
                color: 'rgba(0,0,0,0.6)'
              }}
            >
              {preview.sessions.map((s) => (
                <li key={s.n}>
                  {fmtDate(s.date)} {fmtTime(s.start_time)} — {s.label}
                </li>
              ))}
            </ul>
          </Typography>
        )}
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 16 }}
        >
          The parent gets a payment link on WhatsApp. Nothing is held: the times
          stay open to everyone and the first payment books them.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={adultOk}
              onChange={(e) => setAdultOk(e.target.checked)}
              color="primary"
            />
          }
          label="Parent confirmed an adult will be at home for every session (the teacher sees this on their session screen; the parent app does not show it)"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={submit}
          color="primary"
          variant="contained"
          disabled={!canBook}
        >
          Send payment link
        </Button>
      </DialogActions>
    </Dialog>
  )
}

BookModuleDialog.propTypes = {
  listing: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBooked: PropTypes.func.isRequired
}

export default BookModuleDialog
